import React from 'react';
import Button from '../shared/Button.js';
import InlineNotice from '../shared/InlineNotice.js';
import type {PigConflict} from '../lib/pigOperationsApi.js';

// A pig operation came back `conflict`: someone else changed a field this
// draft also changes. Nothing was saved and the draft is kept. v1 offers ONE
// way forward (coordinator ruling Q2): Use latest — re-read, adopt the other
// person's values for the conflicting fields, keep this draft's other edits.
// There is deliberately no "Keep mine": it would overwrite another person's
// change.

export type PigConflictState = {
  conflicts: PigConflict[];
  labels?: Record<string, string>;
  useLatest: () => Promise<void>;
};

function shown(value: unknown): string {
  if (value && typeof value === 'object' && (value as {__absent?: unknown}).__absent === true) return '(empty)';
  if (value === null || value === undefined || value === '') return '(empty)';
  return String(value);
}

/** The sentence for a conflict: which field, and what it is now. */
export function pigConflictMessage(conflict: Pick<PigConflictState, 'conflicts' | 'labels'>): string {
  const lines = conflict.conflicts.map(
    (c) => `${conflict.labels?.[c.field] || c.field} is now ${shown(c.current)} (someone else changed it).`,
  );
  return ['Not saved: this changed while you were editing.', ...lines].join('\n');
}

export default function PigConflictNotice({conflict}: {conflict: PigConflictState | null}) {
  const [busy, setBusy] = React.useState(false);
  if (!conflict) return null;
  return (
    <div data-pig-conflict="1">
      <InlineNotice
        notice={{kind: 'warning', message: pigConflictMessage(conflict)}}
        action={
          <Button
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void conflict.useLatest().finally(() => setBusy(false));
            }}
          >
            {busy ? 'Loading…' : 'Use latest'}
          </Button>
        }
      />
    </div>
  );
}
